import React, { useCallback, useState, useRef, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
} from "reactflow";
import toast, { Toaster } from 'react-hot-toast';
import dagre from "dagre";
import 'reactflow/dist/style.css';

import AppHeader from './components/AppHeader';
import SidePanel from './components/SidePanel';
import FlowCanvas from './components/FlowCanvas';
import TextView from './components/TextView';
import ClonedNode from './components/ClonedNode';
import LoadingSpinner from './components/LoadingSpinner';
import LoginModal from './components/LoginModal';
import ChartsModal from './components/ChartsModal';
import PromptModal from './components/PromptModal';
import ConfirmModal from './components/ConfirmModal';
import VerifyEmail from './components/VerifyEmail';
import { useAuth } from './contexts/AuthContext';
import { findIsolatedNodes, findCycles, findEmptyNodes } from './utils/graphUtils';

const FlawDisplay = ({ flaws }) => {
  if (!flaws || (flaws.isolatedNodes.length === 0 && flaws.cycles.length === 0 && flaws.emptyNodes.length === 0)) {
    return null;
  }
  const wrapperStyle = {
    position: 'absolute', bottom: '15px', left: '50%', transform: 'translateX(-50%)', zIndex: 10,
    padding: '8px 15px', backgroundColor: '#fff3cd', border: '1px solid #ddd',
    borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  };
  return (
    <div style={wrapperStyle}>
      <p style={{ margin: 0, color: '#856404', fontSize: '14px', fontWeight: '500' }}>
        <strong>論理チェック：</strong>
        {[
          flaws.isolatedNodes.length > 0 && `孤立ノードが${flaws.isolatedNodes.length}件`,
          flaws.cycles.length > 0 && `循環参照が${flaws.cycles.length}件`,
          flaws.emptyNodes.length > 0 && `空のノードが${flaws.emptyNodes.length}件`
        ]
          .filter(Boolean)
          .join("・")}
        {`見つかりました。対象の要素はハイライトされています。`}
      </p>
    </div>
  );
};

const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const nodeWidth = 260;
  const nodeHeight = 150;
  dagreGraph.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 120 });
  nodes.forEach((node) => dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight }));
  edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));
  dagre.layout(dagreGraph);
  return {
    nodes: nodes.map((node) => {
      const { x, y } = dagreGraph.node(node.id);
      return { ...node, position: { x, y } };
    }),
    edges,
  };
};

const MainApp = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [flaws, setFlaws] = useState({ isolatedNodes: [], cycles: [], emptyNodes: [] });
  const [viewMode, setViewMode] = useState('text');
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [isPanelTransitioning, setIsPanelTransitioning] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState({ version: 0, animated: false });
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isChartsModalOpen, setIsChartsModalOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const [currentChartId, setCurrentChartId] = useState(null);
  const [currentChartName, setCurrentChartName] = useState(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [promptModalConfig, setPromptModalConfig] = useState({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({});
  const [textContent, setTextContent] = useState('AIは人間の仕事を奪う可能性がある。なぜなら、単純作業やデータ処理の自動化が進んでいるからだ。実際に、コールセンター業務がAIチャットボットに置き換わる例もある。しかし、AIを管理する新しい職も生まれている。したがって、人間はより創造的な仕事に移行すべきだ。');
  const [draggedNode, setDraggedNode] = useState(null);
  const [isDeleteZoneVisible, setIsDeleteZoneVisible] = useState(false);
  const [isOverDeleteZone, setIsOverDeleteZone] = useState(false);
  const [clonedNode, setClonedNode] = useState(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapper = useRef(null);
  const deleteZoneRef = useRef(null);
  const prevLayoutVersion = useRef(layoutConfig.version);
  const activeToastId = useRef(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const error = urlParams.get('error');

    if (status === 'verified') {
      sessionStorage.setItem('just_verified', 'true');
      showToast('メール認証が完了しました。', 'success');
      setIsLoginModalOpen(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error === 'verification_failed') {
      showToast('メール認証に失敗しました。', 'error');
      setIsLoginModalOpen(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (nodes.length > 0) {
      const isolatedNodes = findIsolatedNodes(nodes, edges);
      const cycles = findCycles(nodes, edges);
      const emptyNodes = findEmptyNodes(nodes);
      setFlaws({ isolatedNodes, cycles, emptyNodes });
    } else {
      setFlaws({ isolatedNodes: [], cycles: [], emptyNodes: [] });
    }
  }, [nodes, edges]);

  useEffect(() => {
    if (!currentUser) {
      setCurrentChartId(null);
      setCurrentChartName(null);
      setNodes([]);
      setEdges([]);
      setTextContent('AIは人間の仕事を奪う可能性がある。なぜなら、単純作業やデータ処理の自動化が進んでいるからだ。実際に、コールセンター業務がAIチャットボットに置き換わる例もある。しかし、AIを管理する新しい職も生まれている。したがって、人間はより創造的な仕事に移行すべきだ。');
      setViewMode('text');
    }
  }, [currentUser]);

  useEffect(() => {
    const layoutVersionChanged = prevLayoutVersion.current !== layoutConfig.version;
    if (reactFlowInstance && viewMode === 'flow' && layoutConfig.version > 0 && nodes.length > 0 && layoutVersionChanged) {
      prevLayoutVersion.current = layoutConfig.version;
      const nodeWidth = 260, nodeHeight = 150, padding = 0.1;
      const { minX, maxX, minY, maxY } = nodes.reduce((acc, node) => ({
        minX: Math.min(acc.minX, node.position.x), maxX: Math.max(acc.maxX, node.position.x + nodeWidth),
        minY: Math.min(acc.minY, node.position.y), maxY: Math.max(acc.maxY, node.position.y + nodeHeight),
      }), { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });
      const bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
      const { width: viewWidth, height: viewHeight } = reactFlowWrapper.current.getBoundingClientRect();
      const panelWidth = isPanelVisible ? 220 : 0;
      const effectiveViewWidth = viewWidth - panelWidth;
      const paddedBounds = {
        width: bounds.width * (1 + padding), height: bounds.height * (1 + padding),
        x: bounds.x - (bounds.width * padding) / 2, y: bounds.y - (bounds.height * padding) / 2,
      };
      const calculatedZoom = Math.min(effectiveViewWidth / paddedBounds.width, viewHeight / paddedBounds.height);
      const zoom = Math.min(Math.max(calculatedZoom, 0.5), 2);
      const rightMargin = 50;
      const availableWidth = effectiveViewWidth - rightMargin;
      const transformX = -paddedBounds.x * zoom + (availableWidth - paddedBounds.width * zoom) / 2 + panelWidth;
      const transformY = -paddedBounds.y * zoom + (viewHeight - paddedBounds.height * zoom) / 2 + 15;
      const duration = layoutConfig.animated ? 300 : 0;
      reactFlowInstance.setViewport({ x: transformX, y: transformY, zoom }, { duration });
    }
  }, [reactFlowInstance, layoutConfig, isPanelVisible, nodes, viewMode]);

  const handleViewChange = (newView) => {
    setViewMode(newView);
  };

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

  const updateNode = useCallback((nodeId, newData) => {
    setNodes((nds) => nds.map((node) => (node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node)));
  }, []);

  const updateEdge = useCallback((edgeId, newLabel) => {
    setEdges((eds) => eds.map((edge) => (edge.id === edgeId ? { ...edge, label: newLabel } : edge)));
  }, []);

  const onConnect = useCallback((connection) => {
    setEdges((eds) => {
      const existingEdge = eds.find((e) => e.source === connection.source && e.target === connection.target);
      if (existingEdge) return eds.filter((e) => e.id !== existingEdge.id);
      const newEdge = { ...connection, id: `${connection.source}-${connection.target}-${+new Date()}`, type: 'custom', label: '理由', data: { onUpdateEdge: updateEdge } };
      return addEdge(newEdge, eds);
    });
  }, [setEdges, updateEdge]);

  const showToast = (message, type = 'default') => {
    if (activeToastId.current) {
      toast.dismiss(activeToastId.current);
    }
    switch (type) {
      case 'success':
        activeToastId.current = toast.success(message);
        break;
      case 'error':
        activeToastId.current = toast.error(message);
        break;
      default:
        activeToastId.current = toast(message);
        break;
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!textContent.trim()) {
      showToast("文章を入力してください。", 'error');
      return;
    }
    setProcessingMessage('フローチャートに変換中...');
    setIsProcessing(true);
    setNodes([]);
    setEdges([]);
    setFlaws({ isolatedNodes: [], cycles: [], emptyNodes: [] });

    try {
      const response = await fetch(`${API_URL}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textContent }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message || 'APIリクエストに失敗しました。');
      }

      const incomingNodes = data.nodes.map((node) => ({ id: node.id, type: 'custom', data: { id: node.id, label: node.text, tag: node.tag }, position: { x: 0, y: 0 } }));
      const incomingEdges = data.edges.map((edge, i) => ({ id: `e${edge.from}-${edge.to}-${i}`, source: edge.from, target: edge.to, label: edge.relation, type: 'custom', data: { onUpdateEdge: updateEdge } }));

      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(incomingNodes, incomingEdges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      setViewMode('flow');
      setLayoutConfig(prev => ({ version: prev.version + 1, animated: false }));
      showToast('フローチャートが生成されました。', 'success');
    } catch (error) {
      console.error('Failed to generate flowchart:', error);
      const errorMessage = error.message || '不明なエラーが発生しました。';
      showToast(`フローチャートの生成に失敗しました: ${errorMessage}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [textContent, updateEdge]);

  const handleSynthesize = useCallback(async () => {
    if (nodes.length === 0) {
      showToast("文章に変換するフローチャートがありません。", 'error');
      return;
    }

    const { isolatedNodes, cycles, emptyNodes } = flaws;
    if (isolatedNodes.length > 0 || cycles.length > 0 || emptyNodes.length > 0) {
      const errorParts = [];
      if (cycles.length > 0) errorParts.push(`循環参照`);
      if (isolatedNodes.length > 0) errorParts.push(`孤立ノード`);
      if (emptyNodes.length > 0) errorParts.push(`空のノード`);
      showToast(`エラー: ${errorParts.join('・')}があります。文章を生成できません。`, 'error');
      return;
    }

    setProcessingMessage('文章に変換中...');
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes, edges }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message || 'APIリクエストに失敗しました。');
      }

      setTextContent(data.text || '文章を生成できませんでした。');
      setViewMode('text');
      showToast('文章が生成されました。', 'success');
    } catch (error) {
      console.error('Failed to synthesize text:', error);
      const errorMessage = error.message || '不明なエラーが発生しました。';
      showToast(`文章の生成に失敗しました: ${errorMessage}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [nodes, edges, flaws]);

  const handleCreateNew = useCallback(() => {
    setTextContent('新しい文章をここに入力してください。');
    setNodes([]);
    setEdges([]);
    setCurrentChartId(null);
    setCurrentChartName(null);
    setViewMode('text');
  }, []);

  const handleSave = useCallback(async (options = {}) => {
    if (!currentUser) {
      showToast('保存するにはログインが必要です。', 'error');
      return;
    }

    const isSaveAsNew = options.saveAsNew || !currentChartId;

    if (isSaveAsNew) {
      setPromptModalConfig({
        title: '新規保存',
        message: 'このチャートの保存名を入力してください:',
        initialValue: currentChartName || '無題のチャート',
        submitText: '保存',
        processingText: '保存中...',
        placeholder: 'チャート名',
        onSubmit: async (chartName) => {
          if (!chartName) {
            showToast('チャート名を入力してください。', 'error');
            return;
          }
          setIsProcessing(true);
          setProcessingMessage('保存中...');
          try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/charts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ name: chartName, textContent, nodes, edges }),
            });
            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error?.message || '保存に失敗しました。');
            }
            const savedChart = await response.json();
            setCurrentChartId(savedChart.id);
            setCurrentChartName(savedChart.name);
            showToast(`「${savedChart.name}」を保存しました。`, 'success');
          } catch (error) {
            console.error('Failed to save new chart:', error);
            showToast(`新規保存に失敗しました: ${error.message}`, 'error');
          } finally {
            setIsProcessing(false);
            setIsPromptModalOpen(false);
          }
        },
      });
      setIsPromptModalOpen(true);
    } else {
      setIsProcessing(true);
      setProcessingMessage('上書き保存中...');
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_URL}/api/charts/${currentChartId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ name: currentChartName, textContent, nodes, edges }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || '上書き保存に失敗しました。');
        }
        const updatedChart = await response.json();
        showToast(`「${updatedChart.name}」を上書き保存しました。`, 'success');
      } catch (error) {
        console.error('Failed to update chart:', error);
        showToast(`上書き保存に失敗しました: ${error.message}`, 'error');
      } finally {
        setIsProcessing(false);
      }
    }
  }, [currentUser, textContent, nodes, edges, currentChartId, currentChartName]);

  const handleLoadChart = useCallback((chart) => {
    if (!chart || !chart.id || !chart.name || !chart.nodes || !chart.edges || !chart.textContent) {
        showToast('無効なチャートデータです。', 'error');
        return;
    }
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(chart.nodes, chart.edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTextContent(chart.textContent);
    setCurrentChartId(chart.id);
    setCurrentChartName(chart.name);
    setIsChartsModalOpen(false);
    setLayoutConfig(prev => ({ version: prev.version + 1, animated: false }));
    showToast(`「${chart.name}」を読み込みました。`, 'success');
  }, []);

  const handleDeleteChart = useCallback((chartId) => {
    return new Promise(async (resolve) => {
      if (!currentUser) {
        showToast('削除するにはログインが必要です。', 'error');
        resolve(false);
        return;
      }

      setConfirmModalConfig({
        title: 'チャートの削除',
        message: 'このチャートを本当に削除しますか？この操作は元に戻せません。',
        confirmText: '削除',
        processingText: '削除中...',
        onConfirm: async () => {
          setIsProcessing(true);
          setProcessingMessage('削除中...');
          try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/charts/${chartId}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error?.message || '削除に失敗しました。');
            }
            showToast('チャートを削除しました。', 'success');
            setIsConfirmModalOpen(false);
            resolve(true);
          } catch (error) {
            console.error('Failed to delete chart:', error);
            showToast(`削除に失敗しました: ${error.message}`, 'error');
            setIsConfirmModalOpen(false);
            resolve(false);
          } finally {
            setIsProcessing(false);
          }
        },
        onCancel: () => {
          setIsConfirmModalOpen(false);
          resolve(false);
        },
      });
      setIsConfirmModalOpen(true);
    });
  }, [currentUser]);

  const handleDeleteAccount = useCallback(async () => {
    if (!currentUser) {
      showToast('アカウントの削除にはログインが必要です。', 'error');
      return;
    }

    setPromptModalConfig({
      title: 'アカウントを削除',
      message: `この操作は元に戻せません。アカウントとすべての関連データ（保存されたチャートなど）が完全に削除されます。\n\nアカウントを削除するには、あなたのメールアドレスを入力してください：`,
      submitText: 'アカウントを削除',
      processingText: '削除中...',
      placeholder: currentUser.email,
      onSubmit: async (confirmationEmail) => {
        if (confirmationEmail.toLowerCase() !== currentUser.email.toLowerCase()) {
          showToast('メールアドレスが一致しません。削除はキャンセルされました。', 'error');
          return;
        }

        setIsProcessing(true);
        setProcessingMessage('アカウントを削除中...');
        try {
          const token = await currentUser.getIdToken();
          const response = await fetch(`${API_URL}/api/user`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || 'アカウントの削除に失敗しました。');
          }
          showToast('アカウントが正常に削除されました。', 'success');
          await logout();
        } catch (error) {
          console.error('Failed to delete account:', error);
          showToast(`アカウントの削除に失敗しました: ${error.message}`, 'error');
        } finally {
          setIsProcessing(false);
          setIsPromptModalOpen(false);
        }
      },
    });
    setIsPromptModalOpen(true);
  }, [currentUser, logout]);

  const handleRenameChart = useCallback(async (newName) => {
    if (!currentUser || !currentChartId) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/charts/${currentChartId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || '名前の変更に失敗しました。');
      }

      showToast('チャート名を更新しました。', 'success');

    } catch (error) {
      console.error('Failed to rename chart:', error);
      showToast(`名前の変更に失敗しました: ${error.message}`, 'error');
    }
  }, [currentUser, currentChartId]);

  const autoLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
    setLayoutConfig(prev => ({ version: prev.version + 1, animated: true }));
  }, [nodes, edges]);

  const togglePanel = useCallback(() => {
    setIsPanelTransitioning(true);
    setIsPanelVisible((v) => !v);
    setTimeout(() => {
      setIsPanelTransitioning(false);
    }, 300);
  }, []);

  const onDragOver = useCallback((event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    if (viewMode !== 'flow') return;
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow');
    if (type && reactFlowInstance) {
      const position = reactFlowInstance.project({ x: event.clientX - reactFlowBounds.left, y: event.clientY - reactFlowBounds.top });
      const newNode = { id: `${+new Date()}`, type: 'custom', position, data: { label: '', tag: type, id: `${+new Date()}` } };
      setNodes((nds) => nds.concat(newNode));
    }
  }, [reactFlowInstance, viewMode]);

  const onNodeDragStart = useCallback(
    (event, node) => {
      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      const viewport = reactFlowInstance.getViewport();
      const paneRect = reactFlowWrapper.current.getBoundingClientRect();

      const screenPosition = {
        top: node.position.y * viewport.zoom + viewport.y + paneRect.top,
        left: node.position.x * viewport.zoom + viewport.x + paneRect.left,
      };

      setClonedNode({
        ...node,
        screenPosition,
        zoom: viewport.zoom,
      });

      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id ? { ...n, data: { ...n.data, isShadowHidden: true } } : n
        )
      );
      setDraggedNode(node);
      setIsOverDeleteZone(false);
    },
    [reactFlowInstance]
  );

  const onNodeDrag = useCallback(
    (event, node) => {
      if (!clonedNode || !reactFlowInstance || !reactFlowWrapper.current) return;

      const viewport = reactFlowInstance.getViewport();
      const paneRect = reactFlowWrapper.current.getBoundingClientRect();

      const screenPosition = {
        top: node.position.y * viewport.zoom + viewport.y + paneRect.top,
        left: node.position.x * viewport.zoom + viewport.x + paneRect.left,
      };

      setClonedNode((prev) => ({
        ...prev,
        screenPosition,
        zoom: viewport.zoom,
      }));
      if (!isDeleteZoneVisible && isPanelVisible) {
        setIsDeleteZoneVisible(true);
      }

      if (deleteZoneRef.current) {
        const deleteZoneBounds = deleteZoneRef.current.getBoundingClientRect();
        const isOver = event.clientX >= deleteZoneBounds.left && event.clientX <= deleteZoneBounds.right &&
                       event.clientY >= deleteZoneBounds.top && event.clientY <= deleteZoneBounds.bottom;
        setIsOverDeleteZone(isOver);
      }
    },
    [clonedNode, reactFlowInstance, isDeleteZoneVisible, isPanelVisible]
  );

  const onNodeDragStop = useCallback((event, node) => {
    let deleted = false;
    if (isDeleteZoneVisible && isOverDeleteZone) {
      if (deleteZoneRef.current) {
        const deleteZoneBounds = deleteZoneRef.current.getBoundingClientRect();
        if (event.clientX >= deleteZoneBounds.left && event.clientX <= deleteZoneBounds.right && event.clientY >= deleteZoneBounds.top && event.clientY <= deleteZoneBounds.bottom) {
          setNodes((nds) => nds.filter((n) => n.id !== node.id));
          setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));
          deleted = true;
        }
      }
    }
    setDraggedNode(null);
    setClonedNode(null);
    if (!deleted) {
      setNodes((nds) =>
        nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, isShadowHidden: false } } : n))
      );
    }
    if (isDeleteZoneVisible) {
      setIsDeleteZoneVisible(false);
    }
    setIsOverDeleteZone(false);
  }, [deleteZoneRef, isDeleteZoneVisible, isOverDeleteZone]);

  const onKeyDown = useCallback((event) => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      const selectedNodes = nodes.filter((n) => n.selected).map((n) => n.id);
      setNodes((nds) => nds.filter((node) => !node.selected));
      setEdges((eds) => eds.filter((edge) => !edge.selected && !selectedNodes.includes(edge.source) && !selectedNodes.includes(edge.target)));
    }
  }, [nodes, edges]);

  const nodesWithFlaws = nodes.map((n) => ({ ...n, data: { ...n.data, onUpdateNode: updateNode, isIsolated: flaws.isolatedNodes.includes(n.id), isEmpty: flaws.emptyNodes.includes(n.id), isCycled: new Set(flaws.cycles.flat()).has(n.id), isShadowHidden: n.data.isShadowHidden, isDragging: !!draggedNode } }));
  const cycledEdgeIds = new Set();
  flaws.cycles.forEach((cycle) => {
    for (let i = 0; i < cycle.length; i++) {
      const source = cycle[i], target = cycle[(i + 1) % cycle.length];
      const edge = edges.find((e) => e.source === source && e.target === target);
      if (edge) cycledEdgeIds.add(edge.id);
    }
  });
  const edgesWithCallbacks = edges.map((e) => ({ ...e, data: { ...e.data, onUpdateEdge: updateEdge }, style: { ...e.style, stroke: cycledEdgeIds.has(e.id) ? '#ff0072' : '#b1b1b7', strokeWidth: cycledEdgeIds.has(e.id) ? 2.5 : 1.5 }, animated: cycledEdgeIds.has(e.id) }));

  return (
    <div className="appContainer">
      {isProcessing && <LoadingSpinner message={processingMessage} />}
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          marginTop: '60px', // デフォルトより下にする
        }}
        toastOptions={{
          style: {
            maxWidth: 'none',   // 幅制限を解除
            whiteSpace: 'nowrap', // 改行を禁止
          },
        }}
      />

      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      <ChartsModal
        isOpen={isChartsModalOpen}
        onClose={() => setIsChartsModalOpen(false)}
        onLoadChart={handleLoadChart}
        onDeleteChart={handleDeleteChart}
      />
      <PromptModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        onSubmit={(value) => {
          if (promptModalConfig.onSubmit) {
            promptModalConfig.onSubmit(value);
          }
        }}
        title={promptModalConfig.title}
        message={promptModalConfig.message}
        initialValue={promptModalConfig.initialValue}
        submitText={promptModalConfig.submitText}
        placeholder={promptModalConfig.placeholder}
        isProcessing={isProcessing}
        processingText={promptModalConfig.processingText}
      />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          if (confirmModalConfig.onCancel) {
            confirmModalConfig.onCancel();
          } else {
            setIsConfirmModalOpen(false);
          }
        }}
        onConfirm={() => {
          if (confirmModalConfig.onConfirm) {
            confirmModalConfig.onConfirm();
          }
        }}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        confirmText={confirmModalConfig.confirmText}
        isProcessing={isProcessing}
        processingText={confirmModalConfig.processingText}
      />
      <AppHeader
        viewMode={viewMode}
        onViewChange={handleViewChange}
        onGenerate={handleGenerate}
        onSynthesize={handleSynthesize}
        isProcessing={isProcessing}
        processingMessage={processingMessage}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onMyChartsClick={() => setIsChartsModalOpen(true)}
        onDeleteAccount={handleDeleteAccount}
        currentChartName={currentChartName}
        onChartNameChange={setCurrentChartName}
        onRenameChart={handleRenameChart}
      />
      <div className="appLayout" onKeyDown={onKeyDown} tabIndex={0}>
        <SidePanel
          isVisible={isPanelVisible}
          togglePanel={togglePanel}
          autoLayout={autoLayout}
          onCreateNew={handleCreateNew}
          onSave={handleSave}
          currentChartId={currentChartId}
          currentUser={currentUser}
          viewMode={viewMode}
          onGenerate={handleGenerate}
          onSynthesize={handleSynthesize}
          isProcessing={isProcessing}
          deleteZoneRef={deleteZoneRef}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          isDeleteZoneActive={isDeleteZoneVisible}
          isOverDeleteZone={isOverDeleteZone}
        />
        <main className={`mainContent ${isPanelVisible ? 'panelVisible' : ''}`}>
          <div style={{ display: viewMode === 'text' ? 'block' : 'none', height: '100%' }}>
            <TextView 
              text={textContent} 
              onTextChange={setTextContent} 
              isPanelVisible={isPanelVisible}
              isTransitioning={isPanelTransitioning}
            />
          </div>
          <div style={{ display: viewMode === 'flow' ? 'block' : 'none', height: '100%' }}>
            <FlawDisplay flaws={flaws} />
            <FlowCanvas
              nodes={nodesWithFlaws}
              edges={edgesWithCallbacks}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onNodeDragStart={onNodeDragStart}
              onNodeDrag={onNodeDrag}
              onNodeDragStop={onNodeDragStop}
              setReactFlowInstance={setReactFlowInstance}
              reactFlowWrapper={reactFlowWrapper}
            />
          </div>
        </main>
        {clonedNode && (
          <div
            style={{
              position: 'fixed',
              top: clonedNode.screenPosition.top,
              left: clonedNode.screenPosition.left,
              zIndex: 1000,
              transform: `scale(${clonedNode.zoom})`,
              transformOrigin: 'top left',
            }}
          >
            <ClonedNode data={clonedNode.data} />
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<MainApp />} />
      <Route path="/verify" element={<VerifyEmail />} />
    </Routes>
  );
}

export default App;